/**
 * StockValuation.jsx — iter173
 *
 * Live valuation of on-hand stock across three cost bases (landed / weighted
 * average / FIFO). Backend returns all three values per SKU so switching the
 * cost basis is instant without a re-query — we just repaint the derived
 * summary + table columns from the row payload.
 */
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ArrowDown, Coins, MagnifyingGlass, Percent, Receipt, Warning } from '@phosphor-icons/react';
import { getStockValuation, downloadStockValuationCsv } from '../lib/api';
import { toast } from 'sonner';

const COST_METHOD_LABEL = {
  landed: 'Landed cost',
  weighted_avg: 'Weighted average',
  fifo: 'FIFO',
};

const fmtMoney = (n) => (n == null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }));
const fmtQty   = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 }));
const fmtPct   = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

export default function StockValuation() {
  const [method, setMethod] = useState('landed');
  const [q, setQ] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchReport = async (nextMethod, nextIncludeZero) => {
    setLoading(true);
    try {
      const r = await getStockValuation({ cost_method: nextMethod, include_zero: nextIncludeZero });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Only refetch when include_zero changes — switching cost basis is
  // repainted client-side (each row already carries all three cost bases).
  useEffect(() => { fetchReport(method, includeZero); }, [includeZero]);

  const activeKey = `cost_value_${method}`;
  const activeUnitKey = `unit_cost_${method}`;

  const rows = data?.rows || [];
  const filteredRows = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => r.sku_code.toLowerCase().includes(needle) || (r.name || '').toLowerCase().includes(needle));
  }, [rows, q]);

  // Because switching cost method is client-side (all 3 values are on each
  // row) we recompute totals from filteredRows so search narrows the summary too.
  const summary = useMemo(() => {
    const s = { on_hand: 0, retail: 0, cost: 0, cost_landed: 0, cost_wavg: 0, cost_fifo: 0 };
    for (const r of filteredRows) {
      s.on_hand += r.on_hand || 0;
      s.retail  += r.retail_value || 0;
      s.cost    += r[activeKey] || 0;
      s.cost_landed += r.cost_value_landed || 0;
      s.cost_wavg   += r.cost_value_weighted_avg || 0;
      s.cost_fifo   += r.cost_value_fifo || 0;
    }
    const margin = s.retail - s.cost;
    const marginPct = s.retail > 0 ? (margin / s.retail) * 100 : null;
    return { ...s, margin, marginPct };
  }, [filteredRows, activeKey]);

  return (
    <Layout>
      <div className="max-w-7xl space-y-5" data-testid="stock-valuation-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="overline">Reports</p>
            <h1 className="text-2xl font-bold">Stock Valuation</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Value all on-hand stock (available + reserved) across landed cost, weighted-average, and FIFO methods.
              Quarantine stock is excluded.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cost basis</Label>
              <Select value={method} onValueChange={(v) => { setMethod(v); }}>
                <SelectTrigger className="w-48" data-testid="cost-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landed">Landed cost</SelectItem>
                  <SelectItem value="weighted_avg">Weighted-average</SelectItem>
                  <SelectItem value="fifo">FIFO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => downloadStockValuationCsv({ cost_method: method, include_zero: includeZero })}
              data-testid="valuation-csv-btn"
            >
              <ArrowDown size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label={`Cost value · ${COST_METHOD_LABEL[method]}`}
            value={fmtMoney(summary.cost)}
            icon={Coins}
            testid="summary-cost"
            accent="brand"
          />
          <SummaryCard
            label="Retail value"
            value={fmtMoney(summary.retail)}
            icon={Receipt}
            testid="summary-retail"
          />
          <SummaryCard
            label="Gross margin"
            value={fmtMoney(summary.margin)}
            icon={Percent}
            testid="summary-margin"
            accent={summary.margin >= 0 ? 'good' : 'bad'}
          />
          <SummaryCard
            label="Margin %"
            value={fmtPct(summary.marginPct)}
            icon={Percent}
            testid="summary-margin-pct"
            accent={(summary.marginPct ?? 0) >= 20 ? 'good' : ((summary.marginPct ?? 0) < 0 ? 'bad' : 'warn')}
          />
        </div>

        {/* Cross-method comparison line */}
        <div className="border border-border bg-muted/40 px-4 py-3 text-xs grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="valuation-comparison">
          <ComparisonCell label="Landed" value={fmtMoney(summary.cost_landed)} active={method === 'landed'} />
          <ComparisonCell label="Weighted-avg" value={fmtMoney(summary.cost_wavg)} active={method === 'weighted_avg'} />
          <ComparisonCell label="FIFO" value={fmtMoney(summary.cost_fifo)} active={method === 'fifo'} />
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-64 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search SKU code or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
              data-testid="valuation-search"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Switch
              checked={includeZero}
              onCheckedChange={setIncludeZero}
              data-testid="valuation-include-zero"
            />
            <span className="text-muted-foreground">Include zero-stock SKUs</span>
          </div>
          <span className="text-[11px] text-muted-foreground ml-auto">
            {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} SKUs · {fmtQty(summary.on_hand)} units on hand
          </span>
        </div>

        {/* Table */}
        <div className="border border-border overflow-x-auto" data-testid="valuation-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2 text-right">On hand</th>
                <th className="px-3 py-2 text-right">Unit cost</th>
                <th className="px-3 py-2 text-right">Cost value</th>
                <th className="px-3 py-2 text-right">Retail value</th>
                <th className="px-3 py-2 text-right">Margin</th>
                <th className="px-3 py-2 text-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Loading valuation…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  <Warning size={16} className="inline mr-1" />
                  No SKUs match this filter.
                </td></tr>
              )}
              {!loading && filteredRows.map((r) => (
                <tr key={r.sku_id} className="border-t border-border hover:bg-muted/20" data-testid={`valuation-row-${r.sku_code}`}>
                  <td className="px-3 py-2 font-mono font-bold">{r.sku_code}</td>
                  <td className="px-3 py-2">
                    <div className="truncate max-w-[240px]" title={r.name}>{r.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{r.inventory_class} · {r.base_uom}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtQty(r.on_hand)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtMoney(r[activeUnitKey])}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono font-bold">{fmtMoney(r[activeKey])}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtMoney(r.retail_value)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-mono ${r.margin_value < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(r.margin_value)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-mono ${(r.margin_pct ?? 0) < 0 ? 'text-red-700' : (r.margin_pct ?? 0) >= 20 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {fmtPct(r.margin_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.generated_at && (
          <p className="text-[10px] text-muted-foreground text-right">
            Generated {new Date(data.generated_at).toLocaleString()} · {data.currency}
          </p>
        )}
      </div>
    </Layout>
  );
}

const SummaryCard = ({ label, value, icon: Icon, testid, accent = 'neutral' }) => {
  const accents = {
    neutral: 'border-border bg-card',
    brand:   'border-brand/40 bg-brand/5',
    good:    'border-emerald-300 bg-emerald-50/60',
    warn:    'border-amber-300 bg-amber-50/60',
    bad:     'border-red-300 bg-red-50/60',
  };
  const iconTint = {
    neutral: 'text-muted-foreground',
    brand: 'text-brand',
    good: 'text-emerald-700',
    warn: 'text-amber-700',
    bad: 'text-red-700',
  };
  return (
    <div className={`border ${accents[accent]} px-4 py-3 rounded-md`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <Icon size={16} weight="fill" className={iconTint[accent]} />
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums font-mono">{value}</p>
    </div>
  );
};

const ComparisonCell = ({ label, value, active }) => (
  <div className={`flex items-baseline justify-between px-2 py-1 ${active ? 'bg-brand/10 border border-brand/40' : ''}`}>
    <span className="uppercase tracking-widest text-[10px] text-muted-foreground">{label}</span>
    <span className={`font-mono tabular-nums font-bold ${active ? 'text-brand' : ''}`}>{value}</span>
  </div>
);
